import {
  Component,
  effect,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';

import {
  PatientModel,
  PatientProgramModel,
  PatientProgramStatus,
  PatientTargetModel,
  SkillAcquisitionObjectiveModel,
  SkillAreaModel,
  TargetType,
} from '../../../core/api/models';
import { SupervisorApiService } from '../../../core/api/services';
import { SupervisorHelperService } from '../../supervisor.helper.service';
import { AlertService } from '../../../shared/services/alert.service';
import { Utils } from '../../../core/utils';
// Import the CKEditor module for Angular integration
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
// Import from our shared configuration
import { Editor, editorConfig } from '../../../shared/ckeditor/ckeditor-config';
import {
  getSparkModeObjectiveDropdownLabel,
  getPatientProgramStatuses,
  getPatientProgramStatusLabel,
} from '../../../shared/utils/utils';
import { formatDate } from '@angular/common';
import { PatientDataFacadeService } from '../../../core/services/patient-data-facade.service';
import { AcademyModeService } from '../../../core/services/academy-mode.service';
import { OverlayPanel } from 'primeng/overlaypanel';

type SkillAcquisitionGoalOption = SkillAcquisitionObjectiveModel & {
  DropdownValue: number | 'other';
  DropdownLabel: string;
};

@Component({
  selector: 'app-add-program',
  templateUrl: './add-program.component.html',
  styleUrl: './add-program.component.css',
  standalone: false,
})
export class AddProgramComponent implements OnInit {
  private readonly otherSkillAcquisitionGoalValue = 'other' as const;

  @Input() selectedPatient: PatientModel | null = null;
  programToEdit: PatientProgramModel | null = null;

  @Output() close = new EventEmitter<boolean>();

  isLoading: boolean = false;

  targets: PatientTargetModel[] | undefined;
  selectedTargets = signal<PatientTargetModel[]>([]);

  statuses: any[PatientProgramStatus];
  selectedStatus = signal<PatientProgramStatus | undefined>(undefined);

  skillAreas: SkillAreaModel[] | undefined;
  selectedSkillArea = signal<SkillAreaModel | undefined>(undefined);

  loading: boolean = false;
  isLoadingTargets: boolean = false;
  targetsLoadError: string | null = null;
  private targetsRefreshRequestId = 0;

  titleName: string = 'Add Program';
  actionName: string = 'Add';

  programId: number = 0;
  enteredName = signal('');
  enteredDetails = signal('');

  skillAcquisitionGoals: SkillAcquisitionObjectiveModel[] | undefined;
  skillAcquisitionGoalOptions: SkillAcquisitionGoalOption[] = [];
  selectedSkillAcquisitionGoalValue = signal<number | 'other' | null>(
    null
  );
  enteredOtherCurrentObjective = signal('');

  dateBaselined = signal<string | null>(null);
  dateClosed = signal<string | null>(null);
  dateMastered = signal<string | null>(null);
  dateOpened = signal<string | null>(null);

  showStatusAnnotationDialog = signal<boolean>(false);
  statusAnnotationText = signal('');
  private pendingPropagateStatus = false;
  private pendingIncludeStatusAnnotation = false;
  readonly statusAnnotationMaxLength = 30;

  @ViewChild('subtargetProgramWarningOp')
  subtargetProgramWarningOp?: OverlayPanel;
  subtargetWarningTarget: PatientTargetModel | null = null;
  readonly subtargetProgramWarningTooltip =
    'This target will not appear for use because it is a sub-target. Click for details.';

  // Using the ClassicEditor from the imported CKEditor module
  public Editor = Editor;

  public editorConfig = editorConfig;

  constructor(
    private supervisorApiService: SupervisorApiService,
    private supervisorHelperService: SupervisorHelperService,
    private alertService: AlertService,
    private patientDataFacade: PatientDataFacadeService,
    public academyModeService: AcademyModeService
  ) {
    effect(() => {
      const newTarget = this.supervisorHelperService.lastCreatedTarget();

      if (newTarget) {
        this.selectedTargets.update(targets => {
          //duplicate
          if (targets.some(t => t.ID === newTarget.ID)) return targets;
          //another patient's target
          if (newTarget.PatientID !== this.selectedPatient?.ID) return targets;
          return [...targets, newTarget];
        });

        // clear after using
        this.supervisorHelperService.lastCreatedTarget.set(null);
        this.rebuildAvailableTargets();
        this.refreshTargets();
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.loadData();
    // Prefer cached targets for instant modal open; refresh when the cache is
    // empty or still holds another patient's targets.
    if (
      !this.supervisorHelperService.targets().length ||
      this.hasStaleCachedTargets()
    ) {
      this.refreshTargets();
    }
  }

  loadData() {
    this.isLoading = true;

    this.rebuildAvailableTargets();

    this.skillAreas = this.supervisorHelperService
      .skillAreas()
      .filter((skillArea) => !skillArea.IsDeleted);

    // Full objective objects from the latest BSP or Academy Assessment.
    this.skillAcquisitionGoals = [
      ...this.supervisorHelperService.skillAcquisitionGoals(),
    ];

    if (
      this.skillAcquisitionGoals?.find((goal) => goal.Goal === 'Other') == null
    ) {
      this.skillAcquisitionGoals.push({
        Goal: 'Other',
        Program: '',
      });
    }

    this.skillAcquisitionGoalOptions = (this.skillAcquisitionGoals ?? []).map(
      (goal) => ({
        ...goal,
        DropdownValue:
          goal.Goal === 'Other' ? this.otherSkillAcquisitionGoalValue : goal.ID!,
        DropdownLabel:
          goal.Goal === 'Other'
            ? 'Other'
            : getSparkModeObjectiveDropdownLabel(
                goal,
                this.academyModeService.getModeKey()
              ),
      })
    );

    this.statuses = getPatientProgramStatuses();
    this.isLoading = false;

    // EDIT LOGIC
    if (this.supervisorHelperService.programToEdit() != null) {
      this.titleName = 'Edit Program';
      this.actionName = 'Save';
      this.programToEdit = this.supervisorHelperService.programToEdit();
      this.programId = this.programToEdit?.ID ?? 0;
      this.enteredName.set(this.programToEdit?.ProgramName ?? '');
      this.enteredDetails.set(this.programToEdit?.Details ?? '');
      if (this.programToEdit?.Targets && this.selectedTargets().length === 0) {
        this.selectedTargets.set(
          this.targetsForSelectedPatient(this.programToEdit.Targets)
        );
      }
      this.selectedSkillArea.set(
        this.skillAreas?.find(
          (skillArea) => skillArea.ID === this.programToEdit?.SkillAreaID
        )
      );
      if (this.programToEdit?.BSP_SkillAcquisitionGoalID != null) {
        this.selectedSkillAcquisitionGoalValue.set(
          this.programToEdit.BSP_SkillAcquisitionGoalID
        );
        this.enteredOtherCurrentObjective.set('');
      } else if (this.programToEdit?.SkillAcquisitionGoal_Other) {
        this.selectedSkillAcquisitionGoalValue.set(
          this.otherSkillAcquisitionGoalValue
        );
        this.enteredOtherCurrentObjective.set(
          this.programToEdit?.SkillAcquisitionGoal_Other ?? ''
        );
      } else {
        this.selectedSkillAcquisitionGoalValue.set(null);
        this.enteredOtherCurrentObjective.set('');
      }

      this.selectedStatus.set(this.programToEdit?.ProgramStatus);
      if (this.programToEdit?.DateBaselined) {
        this.dateBaselined.set(
          formatDate(this.programToEdit.DateBaselined, 'yyyy-MM-dd', 'en-US')
        );
      }
      if (this.programToEdit?.DateClosed) {
        this.dateClosed.set(
          formatDate(this.programToEdit.DateClosed, 'yyyy-MM-dd', 'en-US')
        );
      }
      if (this.programToEdit?.DateMastered) {
        this.dateMastered.set(
          formatDate(this.programToEdit.DateMastered, 'yyyy-MM-dd', 'en-US')
        );
      }
      if (this.programToEdit?.DateOpened) {
        this.dateOpened.set(
          formatDate(this.programToEdit.DateOpened, 'yyyy-MM-dd', 'en-US')
        );
      }
      if (this.programToEdit?.ShouldDisplayAnnotationForStatus) {
        this.statusAnnotationText.set(
          this.programToEdit.StatusAnnotationText ?? ''
        );
      }
    }

    this.rebuildAvailableTargets();
  }

  shouldShowStatusAnnotationEditor(): boolean {
    const status = this.selectedStatus();
    const isClosedOrOnHold =
      status === PatientProgramStatus.Closed ||
      status === PatientProgramStatus.On_Hold;

    return (
      !!this.programToEdit?.ShouldDisplayAnnotationForStatus && isClosedOrOnHold
    );
  }

  onCancel() {
    this.close.emit(false);
  }

  onSubmit() {
    if (!this.loading) {
      //Check if changing program status
      if (this.selectedStatus() != this.programToEdit?.ProgramStatus) {
        this.alertService
          .confirm(
            'Changing program status',
            'You´re changing the program status. Do you want to change also its targets statuses?'
          )
          .subscribe((propagateStatus) => {
            this.continueStatusChangeAfterPropagateConfirm(!!propagateStatus);
          });
      } else {
        this.saveProgram(false, false, false);
      }
    }
  }

  private continueStatusChangeAfterPropagateConfirm(propagateStatus: boolean) {
    const newStatus = this.selectedStatus();
    const isClosedOrOnHold =
      newStatus === PatientProgramStatus.Closed ||
      newStatus === PatientProgramStatus.On_Hold;

    if (!isClosedOrOnHold) {
      this.saveProgram(false, propagateStatus, false, false, null);
      return;
    }

    this.pendingPropagateStatus = propagateStatus;
    this.pendingIncludeStatusAnnotation = false;
    this.statusAnnotationText.set(this.buildDefaultStatusAnnotationText());
    this.showStatusAnnotationDialog.set(true);
  }

  statusAnnotationDialogTitle(): string {
    return this.selectedStatus() === PatientProgramStatus.On_Hold
      ? 'Moving Program to On Hold'
      : 'Moving Program to Closed';
  }

  private buildDefaultStatusAnnotationText(): string {
    const programName = (this.enteredName() || 'Program').trim();
    const statusSuffix =
      this.selectedStatus() === PatientProgramStatus.On_Hold
        ? 'on hold'
        : 'closed';
    const defaultText = `${programName} ${statusSuffix}`;
    return defaultText.substring(0, this.statusAnnotationMaxLength);
  }

  confirmStatusAnnotationDialog() {
    const reason = (this.statusAnnotationText() || '').trim();
    if (!reason) {
      this.alertService.error('Please enter a reason for the annotation.');
      return;
    }

    if (reason.length > this.statusAnnotationMaxLength) {
      this.alertService.error(
        `Annotation reason must be ${this.statusAnnotationMaxLength} characters or fewer.`
      );
      return;
    }

    this.showStatusAnnotationDialog.set(false);
    this.pendingIncludeStatusAnnotation = true;
    this.saveProgram(
      false,
      this.pendingPropagateStatus,
      false,
      true,
      reason.substring(0, this.statusAnnotationMaxLength)
    );
  }

  cancelStatusAnnotationDialog() {
    this.showStatusAnnotationDialog.set(false);
    this.pendingIncludeStatusAnnotation = false;
    this.saveProgram(false, this.pendingPropagateStatus, false, false, null);
  }

  onSelectedTargetsDropdownClick() {
    // Refresh latest targets right when user opens the selector, not on modal open.
    this.refreshTargets();
    const event = new Event('targets-dropdown-clicked');
    document.getElementById('addProgramForm')?.dispatchEvent(event);
  }

  onSelectedSkillAreaDropdownClick() {
    const event = new Event('skill-area-dropdown-clicked');
    document.getElementById('addProgramForm')?.dispatchEvent(event);
  }

  onSelectedStatusDropdownClick() {
    const event = new Event('status-dropdown-clicked');
    document.getElementById('addProgramForm')?.dispatchEvent(event);
  }

  onSelectedSkillAcquisitionGoalDropdownClick() {
    const event = new Event('skill-acquisition-goal-dropdown-clicked');
    document.getElementById('addProgramForm')?.dispatchEvent(event);
  }

  onSkillAcquisitionGoalChange(value: number | 'other' | null) {
    this.selectedSkillAcquisitionGoalValue.set(value);

    if (value !== this.otherSkillAcquisitionGoalValue) {
      this.enteredOtherCurrentObjective.set('');
    }
  }

  selectedSkillAcquisitionGoal() {
    if (this.selectedSkillAcquisitionGoalValue() == null) {
      return undefined;
    }

    return this.skillAcquisitionGoalOptions.find(
      (skillAcquisitionGoal) =>
        skillAcquisitionGoal.DropdownValue ===
        this.selectedSkillAcquisitionGoalValue()
    );
  }

  isOtherSkillAcquisitionGoalSelected() {
    return (
      this.selectedSkillAcquisitionGoalValue() ===
      this.otherSkillAcquisitionGoalValue
    );
  }

  selectedSkillAcquisitionGoalId(): number | null | undefined {
    if (this.isOtherSkillAcquisitionGoalSelected()) {
      return null;
    }

    const selectedValue = this.selectedSkillAcquisitionGoalValue();
    return typeof selectedValue === 'number' ? selectedValue : undefined;
  }

  getPatientProgramStatusLabel(programStatus: PatientProgramStatus) {
    return getPatientProgramStatusLabel(programStatus);
  }

  getTargetTypeLabel(targetType: TargetType) {
    return Utils.patientTargetTypeLabel(targetType);
  }

  private resolveFullTarget(
    target: PatientTargetModel
  ): PatientTargetModel | undefined {
    if (target.ID == null) {
      return undefined;
    }
    return this.supervisorHelperService
      .targets()
      .find((t) => t.ID === target.ID);
  }

  isSelectedTargetSubtarget(target: PatientTargetModel): boolean {
    return this.resolveFullTarget(target)?.IsTargetSubtarget === true;
  }

  getParentTargetFor(
    target: PatientTargetModel
  ): PatientTargetModel | undefined {
    if (target.ID == null) {
      return undefined;
    }
    return this.supervisorHelperService
      .targets()
      .find((parent) =>
        parent.SubTargets?.some((subTarget) => subTarget.ID === target.ID)
      );
  }

  toggleSubtargetProgramWarning(
    event: Event,
    target: PatientTargetModel
  ): void {
    event.preventDefault();
    event.stopPropagation();
    this.subtargetWarningTarget = target;
    this.subtargetProgramWarningOp?.toggle(event);
  }

  editParentTargetFromWarning(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const parent = this.subtargetWarningTarget
      ? this.getParentTargetFor(this.subtargetWarningTarget)
      : undefined;
    this.subtargetProgramWarningOp?.hide();
    if (parent) {
      this.supervisorHelperService.additTargetButtonClicked(parent);
    }
  }

  /** Targets from another patient must never be assignable to this program. */
  private targetsForSelectedPatient(
    targets: PatientTargetModel[]
  ): PatientTargetModel[] {
    const patientID = this.selectedPatient?.ID;
    if (patientID == null) {
      return [];
    }
    return targets.filter((t) => t.PatientID === patientID);
  }

  private hasStaleCachedTargets(): boolean {
    const cached = this.supervisorHelperService.targets();
    return (
      cached.length > 0 &&
      this.targetsForSelectedPatient(cached).length !== cached.length
    );
  }

  private rebuildAvailableTargets(): void {
    this.targets = this.targetsForSelectedPatient(
      this.supervisorHelperService.targets()
    )
      .filter((t) => !t.IsDeleted && !t.IsArchived && !t.IsTargetSubtarget)
      .filter((t) => !this.selectedTargets().some((st) => st.ID === t.ID))
      .sort((a, b) => {
        const aIndex = this.selectedTargets().findIndex((t) => t.ID === a.ID);
        const bIndex = this.selectedTargets().findIndex((t) => t.ID === b.ID);
        if (aIndex == -1 && bIndex == -1) return 0;
        if (aIndex == -1) return 1;
        if (bIndex == -1) return -1;
        return aIndex - bIndex;
      });
  }

  onTargetsMovedToProgram(items: PatientTargetModel[] | undefined): void {
    if (!items || items.length === 0) {
      return;
    }

    // Sub-targets cannot be newly assigned to programs
    const allowedItems = items.filter(
      (item) => !this.isSelectedTargetSubtarget(item)
    );
    if (allowedItems.length === 0) {
      this.rebuildAvailableTargets();
      return;
    }

    const movedIds = new Set(
      allowedItems
        .map((item) => item.ID)
        .filter((id): id is number => id != null)
    );

    this.selectedTargets.update((current) => {
      const existingIds = new Set(
        current.map((item) => item.ID).filter((id): id is number => id != null)
      );
      const toAdd = allowedItems.filter(
        (item) => !existingIds.has(item.ID ?? -1)
      );
      return toAdd.length > 0 ? [...current, ...toAdd] : current;
    });

    this.rebuildAvailableTargets();
  }

  onTargetsRemovedFromProgram(items: PatientTargetModel[] | undefined): void {
    if (!items || items.length === 0) {
      return;
    }

    const removedIds = new Set(
      items.map((item) => item.ID).filter((id): id is number => id != null)
    );

    this.selectedTargets.update((current) =>
      current.filter((item) => !removedIds.has(item.ID ?? -1))
    );

    // Rebuild so legacy sub-targets stay out of the available source list
    this.rebuildAvailableTargets();
  }

  refreshTargets(): void {
    if (!this.selectedPatient?.ID) {
      return;
    }

    const requestId = ++this.targetsRefreshRequestId;
    this.isLoadingTargets = true;
    this.targetsLoadError = null;

    this.patientDataFacade
      .getTargets(this.selectedPatient.ID, {
        onlyActive: false,
      })
      .subscribe({
        next: (targets) => {
          if (requestId !== this.targetsRefreshRequestId) {
            return;
          }

          this.supervisorHelperService.targets.set(targets ?? []);
          this.rebuildAvailableTargets();
          this.isLoadingTargets = false;
        },
        error: () => {
          if (requestId !== this.targetsRefreshRequestId) {
            return;
          }

          this.isLoadingTargets = false;
          this.targetsLoadError =
            'Failed to load the latest targets. Please retry.';
        },
      });
  }

  saveProgram(
    isArchive: boolean = false,
    propagateStatus: boolean = false,
    propagateArchive: boolean = false,
    includeStatusAnnotation: boolean = false,
    statusAnnotationText: string | null = null
  ) {
    if (!this.loading) {
      const shouldSaveExistingAnnotationEdit =
        !includeStatusAnnotation &&
        this.shouldShowStatusAnnotationEditor() &&
        this.selectedStatus() === this.programToEdit?.ProgramStatus;
      const annotationTextToSave = includeStatusAnnotation
        ? statusAnnotationText
        : shouldSaveExistingAnnotationEdit
          ? (this.statusAnnotationText() || '').trim().substring(
              0,
              this.statusAnnotationMaxLength
            )
          : null;
      const shouldIncludeStatusAnnotation =
        includeStatusAnnotation ||
        (shouldSaveExistingAnnotationEdit && !!annotationTextToSave);

      if (shouldSaveExistingAnnotationEdit && !annotationTextToSave) {
        this.alertService.error('Please enter a reason for the annotation.');
        return;
      }

      this.loading = true;
      this.supervisorApiService
        .SavePatientProgram({
          body: {
            PatientProgram: {
              ID: this.programId,
              Details: this.enteredDetails(),
              PatientID: this.selectedPatient?.ID ?? undefined,
              ProgramName: this.enteredName(),
              SkillAreaID: this.selectedSkillArea()?.ID,
              SkillAreaName: this.selectedSkillArea()?.SkillAreaName,
              Targets: this.selectedTargets(),
              IsArchived: isArchive,
              ProgramStatus: this.selectedStatus() ?? PatientProgramStatus.Ready,
              BSP_SkillAcquisitionGoalID:
                this.selectedSkillAcquisitionGoalId(),
              SkillAcquisitionGoal_Other:
                this.isOtherSkillAcquisitionGoalSelected()
                  ? this.enteredOtherCurrentObjective()
                  : null,
              DateBaselined: this.dateBaselined(),
              DateClosed: this.dateClosed(),
              DateMastered: this.dateMastered(),
              DateOpened: this.dateOpened(),
              ShouldDisplayAnnotationForStatus: shouldIncludeStatusAnnotation,
              StatusAnnotationText: shouldIncludeStatusAnnotation
                ? annotationTextToSave
                : null,
              StatusAnnotationDate: null,
            },
            PropagateStatus: propagateStatus,
            PropagateArchieve: propagateArchive,
          },
        })
        .subscribe(
          (patientProgram) => {
            console.log(patientProgram);
            this.loading = false;
            this.alertService.success('Program saved successfully');
            this.close.emit(true);
          },
          (error) => {
            console.error(error);
            this.alertService.error(
              Utils.getErrorMessageFromErrorResponse(error)
            );
            this.loading = false;
            this.close.emit(false);
          }
        );
    }
  }

  addTarget(event: Event) {
    console.log('addTarget');
    event.preventDefault();
    this.supervisorHelperService.additTargetButtonClicked(null, this.programId);
  }

  archiveButtonClicked() {
    if (!this.loading) {
      if (this.programToEdit?.IsArchived) {
        this.programToEdit!.IsArchived = false;
        this.saveProgram(false, false, false);
      } else {
        const confirm = this.alertService
          .confirm(
            'Archiving program',
            'You´re archiving the program. Do you want to also archive its targets?'
          )
          .subscribe((result) => {
            if (result) {
              this.saveProgram(true, false, true);
            } else {
              this.saveProgram(true, false, false);
            }
          });
      }
    }
  }

  onDetailsChange(event: any) {
    this.enteredDetails.set(event.editor.getData());
  }
}
